/**
 * Action Register verb builders (13-04): the verbs as pure data (13-RESEARCH Pattern 3).
 *
 * Each verb is `(row, actor, input, now) → { patch, audit[] }`:
 *   - `patch` holds ONLY the fields that change (SP internal names; Person columns as
 *     `<Name>LookupId`), so the PATCH and the audit drafts are one-to-one;
 *   - `audit` holds one `StateChange` draft per changed field (09.1-CONTEXT §5: the writer of the
 *     change writes the audit row). `ActorLookupId` is present only when the actor's site-user id
 *     resolved; the UPN is always in `Note` (D-13-00-4).
 *
 * Every Action Register mutation in the app goes through these builders, so the audit contract
 * and E3 (09.1 §7, D-13-00-3) are proven once, here, with no SharePoint.
 *
 * Permissions are NOT checked here — that is 13-05 (`actionAuthority.ts`). An invalid
 * TRANSITION is a programming error, not an authority question, so it throws `VerbError`.
 *
 * Plan-time semantics (D-13-04-*): reject keeps `CompletedAt`; decide sets `Verified` directly;
 * a regulatory reason goes to the `DecidedOption` audit Note and is appended to `Evidence`;
 * reopen is the same Melbourne calendar day only.
 *
 * No I/O, no React, no services. No date library: Melbourne days come from `Intl`.
 */

import { AR_FIELDS, AR_LOOKUP_FIELDS, OPEN_STATUSES, OWNER_ROLES, actionRef } from '../config/actionRegister';
import type {
  ActionRow,
  ActionStatus,
  ActionVerb,
  ActionVerdict,
  Actor,
  AuditRowDraft,
  Escalation,
  OwnerRole,
  VerbResult,
} from '../types/actionRegister';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type VerbErrorCode =
  | 'terminal'
  | 'invalid-transition'
  | 'already-accepted'
  | 'already-released'
  | 'evidence-required'
  | 'not-a-decision'
  | 'not-verifiable'
  | 'invalid-verdict'
  | 'option-required'
  // decide: DecidedOption is a 255-character Text column (13-10)
  | 'option-too-long'
  | 'reason-required'
  | 'note-required'
  | 'reopen-window-closed'
  | 'unknown-verb'
  // reassign (ISS-186, D-13-R-1)
  | 'owner-role-required'
  // Verification Log verbs (13-11, lib/vlVerbs.ts)
  | 'already-verified'
  | 'self-verification'
  | 'oversight-only'
  // 13-11.1: the widened verifier (D-13-R-6) and vlCorrect (D-13-R-5)
  | 'not-verifier-role'
  | 'not-verifier'
  | 'outside-window'
  | 'no-change'
  | 'no-verdict';

/** A verb refused on state-machine grounds. `code` is stable; the message is for developers. */
export class VerbError extends Error {
  readonly code: VerbErrorCode;

  constructor(code: VerbErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'VerbError';
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** Everything any verb may read from the caller. Each verb reads only its own fields. */
export interface VerbInput {
  /** Carried on every audit row. Required as the reason for `reject`; the note for `retire`
   *  and a bulk `release`. */
  note?: string;
  /** `done`: the proof. Required when `VerificationMethod = evidence`. */
  evidence?: string;
  /** `verify`: `sampled-ok` instead of `verified`, on a `sampled` row only. */
  verdict?: Extract<ActionVerdict, 'verified' | 'sampled-ok'>;
  /** `reject`: prior rejections of this row, from `countRejections(auditRows)`. */
  priorRejections?: number;
  /** `decide`: the chosen option. */
  option?: string;
  /** `decide`: the reason. Required when `regulatory === true`. */
  reason?: string;
  /** `decide`: the row is a Regulatory Decision (13-05 classifies; fail-safe). */
  regulatory?: boolean;
  /** `release`: part of a bulk release sitting (the note becomes required). */
  bulk?: boolean;
  /** `reassign`: the new owner role (required). */
  ownerRole?: OwnerRole;
  /** `reassign`: the new owner's site user id (optional; blank leaves the row to the role). */
  ownerLookupId?: string | null;
}

/** The instant the human acted (the tap time). A Date or anything `new Date()` parses. */
export type ActedAt = Date | string;

export type VerbBuilder = (row: ActionRow, actor: Actor, input: VerbInput, now: ActedAt) => VerbResult;

// ---------------------------------------------------------------------------
// Fields: patch key → audit column name + how to read the current value
// ---------------------------------------------------------------------------

const F = AR_FIELDS;
const L = AR_LOOKUP_FIELDS;

interface FieldSpec {
  /** The audit `FieldChanged`: the column's internal name (`Owner`, not `OwnerLookupId`). */
  column: string;
  read: (row: ActionRow) => unknown;
  isDate?: boolean;
}

const FIELD_SPECS: Record<string, FieldSpec> = {
  [F.status]: { column: F.status, read: (r) => r.status },
  [F.acceptedAt]: { column: F.acceptedAt, read: (r) => r.acceptedAt, isDate: true },
  [F.completedAt]: { column: F.completedAt, read: (r) => r.completedAt, isDate: true },
  [F.verifiedAt]: { column: F.verifiedAt, read: (r) => r.verifiedAt, isDate: true },
  [F.releasedAt]: { column: F.releasedAt, read: (r) => r.releasedAt, isDate: true },
  [L.owner]: { column: F.owner, read: (r) => r.ownerLookupId },
  [F.ownerRole]: { column: F.ownerRole, read: (r) => r.ownerRole },
  [L.verifiedBy]: { column: F.verifiedBy, read: (r) => r.verifiedByLookupId },
  [F.evidence]: { column: F.evidence, read: (r) => r.evidence },
  [F.verdict]: { column: F.verdict, read: (r) => r.verdict },
  [F.escalation]: { column: F.escalation, read: (r) => r.escalation },
  [F.decidedOption]: { column: F.decidedOption, read: (r) => r.decidedOption },
  [F.releasedBy]: { column: F.releasedBy, read: (r) => r.releasedBy },
  [F.releaseNote]: { column: F.releaseNote, read: (r) => r.releaseNote },
};

/** null, undefined and '' are the same "empty"; everything else compares as a string. */
function norm(value: unknown, isDate = false): string {
  if (value === null || value === undefined || value === '') return '';
  if (isDate) {
    const t = Date.parse(String(value));
    if (!Number.isNaN(t)) return new Date(t).toISOString();
  }
  return String(value);
}

function auditValue(value: unknown): string | null {
  return value === null || value === undefined || value === '' ? null : String(value);
}

function toIso(now: ActedAt): string {
  const d = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(d.getTime())) throw new TypeError(`invalid acted-at time: ${String(now)}`);
  return d.toISOString();
}

export interface DiffContext {
  verb: ActionVerb;
  actor: Actor;
  /** ISO UTC; becomes every draft's `EntryTimestamp`. */
  actedAt: string;
  /** Per audit column, extra Note text after the verb line (e.g. the decision reason). */
  noteFor?: Partial<Record<string, string>>;
  /** Carried on every row after the verb line. */
  note?: string;
}

function composeNote(ctx: DiffContext, column: string): string {
  const extra = [ctx.note, ctx.noteFor?.[column]]
    .map((s) => s?.trim())
    .filter((s): s is string => !!s);
  const head = `${ctx.verb} by ${ctx.actor.upn}`;
  return extra.length ? `${head}: ${extra.join('; ')}` : head;
}

/**
 * The changed subset of `proposed` against `row`, plus one `StateChange` draft per changed field.
 * Unchanged fields are dropped from the patch as well, so patch keys ↔ audit rows one-to-one.
 */
export function diff(row: ActionRow, proposed: Record<string, unknown>, ctx: DiffContext): VerbResult {
  const patch: Record<string, unknown> = {};
  const audit: AuditRowDraft[] = [];
  const parentRef = actionRef(row.id);

  for (const [key, next] of Object.entries(proposed)) {
    const spec = FIELD_SPECS[key];
    if (!spec) throw new Error(`actionVerbs: no field spec for patch key '${key}'`);
    const prev = spec.read(row);
    if (norm(prev, spec.isDate) === norm(next, spec.isDate)) continue;

    patch[key] = next;
    const draft: AuditRowDraft = {
      title: `${ctx.verb} ${parentRef}: ${spec.column}`,
      parentRef,
      entryTimestamp: ctx.actedAt,
      entryType: 'StateChange',
      fieldChanged: spec.column,
      oldValue: auditValue(prev),
      newValue: auditValue(next),
      note: composeNote(ctx, spec.column),
    };
    if (ctx.actor.lookupId) draft.actorLookupId = ctx.actor.lookupId;
    audit.push(draft);
  }
  return { patch, audit };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Closed statuses no verb leaves. `Rejected` is a legacy 09.1 status; E3 never sets it. */
const TERMINAL: readonly ActionStatus[] = ['Verified', 'Rejected', 'Retired'];

function isOpen(status: ActionStatus): boolean {
  return (OPEN_STATUSES as readonly string[]).includes(status);
}

/** Throws `terminal` on a closed row, else `invalid-transition` unless `status ∈ from`. */
function requireFrom(verb: ActionVerb, row: ActionRow, from: readonly ActionStatus[]): void {
  if (TERMINAL.includes(row.status)) {
    throw new VerbError('terminal', `${verb}: ACT-${row.id} is ${row.status}`);
  }
  if (!from.includes(row.status)) {
    throw new VerbError('invalid-transition', `${verb}: not allowed from ${row.status}`);
  }
}

function text(s: string | undefined): string {
  return (s ?? '').trim();
}

const ESCALATION_RANK: Record<Escalation, number> = { none: 0, manager: 1, brief: 2 };

/** The higher of two escalation levels (never downgrade). */
function maxEscalation(a: Escalation, b: Escalation): Escalation {
  return ESCALATION_RANK[b] > ESCALATION_RANK[a] ? b : a;
}

const MELBOURNE_DAY = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The `Australia/Melbourne` calendar date of an instant, as `YYYY-MM-DD` (DST-safe). */
export function melbourneDay(iso: ActedAt): string {
  const parts = MELBOURNE_DAY.formatToParts(new Date(toIso(iso)));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Prior rejections of a row, counted from its audit trail (D-13-00-3: no counter column exists).
 * A rejection is a `Verdict` change whose new value is `rejected`.
 */
export function countRejections(
  auditRows: readonly Pick<AuditRowDraft, 'fieldChanged' | 'newValue'>[],
): number {
  return auditRows.filter(
    (a) => a.fieldChanged === F.verdict && (a.newValue ?? '').trim() === 'rejected',
  ).length;
}

// ---------------------------------------------------------------------------
// Verbs
// ---------------------------------------------------------------------------

/** Proposed → Accepted. Fills `Owner` with the actor when known and the row has none. */
export const accept: VerbBuilder = (row, actor, input, now) => {
  if (row.status === 'Accepted' || row.status === 'InProgress') {
    throw new VerbError('already-accepted', `accept: ACT-${row.id} is already ${row.status}`);
  }
  requireFrom('accept', row, ['Proposed']);
  const at = toIso(now);
  const proposed: Record<string, unknown> = {
    [F.status]: 'Accepted',
    [F.acceptedAt]: at,
  };
  if (actor.lookupId && !row.ownerLookupId) proposed[L.owner] = actor.lookupId;
  return diff(row, proposed, { verb: 'accept', actor, actedAt: at, note: input.note });
};

/**
 * Proposed / Accepted / InProgress → Done. From Proposed an accept is folded in (its own audit
 * rows). An `evidence` row requires `input.evidence`. A row returned by a rejection has its
 * `Verdict` reset to `pending`, so the next verdict is a real, audited change (E3 counting).
 */
export const done: VerbBuilder = (row, actor, input, now) => {
  requireFrom('done', row, ['Proposed', 'Accepted', 'InProgress']);
  const at = toIso(now);
  const evidence = text(input.evidence);
  if (row.verificationMethod === 'evidence' && !evidence) {
    throw new VerbError('evidence-required', `done: ACT-${row.id} needs evidence`);
  }
  const proposed: Record<string, unknown> = {
    [F.status]: 'Done',
    [F.completedAt]: at,
  };
  if (row.status === 'Proposed') {
    proposed[F.acceptedAt] = at;
    if (actor.lookupId && !row.ownerLookupId) proposed[L.owner] = actor.lookupId;
  }
  if (evidence) proposed[F.evidence] = evidence;
  if (row.verdict !== 'pending') proposed[F.verdict] = 'pending';
  return diff(row, proposed, { verb: 'done', actor, actedAt: at, note: input.note });
};

/** Proposed / Accepted → NotMine (E6 bounce). Clears `Owner` when one was set. */
export const notMine: VerbBuilder = (row, actor, input, now) => {
  requireFrom('notMine', row, ['Proposed', 'Accepted']);
  const at = toIso(now);
  const proposed: Record<string, unknown> = { [F.status]: 'NotMine' };
  if (row.ownerLookupId) proposed[L.owner] = null;
  return diff(row, proposed, { verb: 'notMine', actor, actedAt: at, note: input.note });
};

function requireVerifiable(verb: ActionVerb, row: ActionRow): void {
  requireFrom(verb, row, ['Done']);
  if (row.kind !== 'Action' && row.kind !== 'Approval') {
    throw new VerbError('not-verifiable', `${verb}: a ${row.kind} is verified by decide`);
  }
}

/** Done → Verified on an Action / Approval. A `sampled` row may record `sampled-ok`. */
export const verify: VerbBuilder = (row, actor, input, now) => {
  requireVerifiable('verify', row);
  const verdict = input.verdict ?? 'verified';
  if (verdict === 'sampled-ok' && row.verificationMethod !== 'sampled') {
    throw new VerbError('invalid-verdict', `verify: sampled-ok needs VerificationMethod = sampled`);
  }
  if (verdict !== 'verified' && verdict !== 'sampled-ok') {
    throw new VerbError('invalid-verdict', `verify: unknown verdict ${String(verdict)}`);
  }
  const at = toIso(now);
  const proposed: Record<string, unknown> = {
    [F.status]: 'Verified',
    [F.verdict]: verdict,
    [F.verifiedAt]: at,
  };
  if (actor.lookupId) proposed[L.verifiedBy] = actor.lookupId;
  return diff(row, proposed, { verb: 'verify', actor, actedAt: at, note: input.note });
};

/**
 * E3. Done → `Verdict = rejected`, `Status = Accepted` (owner redo). `CompletedAt` is kept
 * (D-13-04-1). The reason (`input.note`) is required. With one or more prior rejections (from
 * `countRejections`), `Escalation` rises to `manager` — never downgraded from `brief`.
 *
 * ISS-204 / D-13-R-8 (Andy, 4 Oct 2026, "a"): an UNRELEASED Done row (reject is exempt from the
 * release gate there, D-13-R-7) is released in the same patch, so "returned to its owner" is true:
 * `ReleasedAt = now`, `ReleasedBy = <actor UPN>` (Text, D-13-01-1), `ReleaseNote = <the reason>`,
 * each audited like every other changed field. An already-released row's release is untouched.
 */
export const reject: VerbBuilder = (row, actor, input, now) => {
  requireVerifiable('reject', row);
  const reason = text(input.note);
  if (!reason) {
    throw new VerbError('reason-required', `reject: ACT-${row.id} needs a reason`);
  }
  const at = toIso(now);
  const proposed: Record<string, unknown> = {
    [F.verdict]: 'rejected',
    [F.status]: 'Accepted',
    [F.verifiedAt]: at,
  };
  if (actor.lookupId) proposed[L.verifiedBy] = actor.lookupId;
  if ((input.priorRejections ?? 0) >= 1) {
    proposed[F.escalation] = maxEscalation(row.escalation, 'manager');
  }
  if (!text(row.releasedAt ?? undefined)) {
    proposed[F.releasedAt] = at;
    proposed[F.releasedBy] = actor.upn;
    proposed[F.releaseNote] = reason;
  }
  return diff(row, proposed, { verb: 'reject', actor, actedAt: at, note: reason });
};

/**
 * Proposed / Accepted → Verified on a Decision: the decision is its own verification (09.1 §2,
 * D-13-04-2). A regulatory decision requires a reason, which goes to the `DecidedOption` audit
 * row's Note and is appended to `Evidence` (D-13-04-3).
 */
export const decide: VerbBuilder = (row, actor, input, now) => {
  if (row.kind !== 'Decision') {
    throw new VerbError('not-a-decision', `decide: ACT-${row.id} is a ${row.kind}`);
  }
  requireFrom('decide', row, ['Proposed', 'Accepted']);
  const option = text(input.option);
  if (!option) throw new VerbError('option-required', `decide: no option chosen`);
  // A Text value over 255 fails the whole PATCH live; refuse it here (13-10, Rule 2).
  if (option.length > 255) throw new VerbError('option-too-long', `decide: DecidedOption is 255 characters at most`);
  const reason = text(input.reason);
  if (input.regulatory === true && !reason) {
    throw new VerbError('reason-required', `decide: a regulatory decision needs a reason`);
  }
  const at = toIso(now);
  const proposed: Record<string, unknown> = {
    [F.decidedOption]: option,
    [F.status]: 'Verified',
    [F.verdict]: 'verified',
    [F.completedAt]: at,
    [F.verifiedAt]: at,
  };
  if (actor.lookupId) proposed[L.verifiedBy] = actor.lookupId;
  if (reason) {
    const line = `Decision reason (${melbourneDay(at)}, ${actor.upn}): ${reason}`;
    const prior = (row.evidence ?? '').trimEnd();
    proposed[F.evidence] = prior ? `${prior}\n${line}` : line;
  }
  return diff(row, proposed, {
    verb: 'decide',
    actor,
    actedAt: at,
    note: input.note,
    noteFor: reason ? { [F.decidedOption]: reason } : undefined,
  });
};

/** An open, unreleased row → released to its owner. The note is required for a bulk release. */
export const release: VerbBuilder = (row, actor, input, now) => {
  if (TERMINAL.includes(row.status)) {
    throw new VerbError('terminal', `release: ACT-${row.id} is ${row.status}`);
  }
  if (row.releasedAt) {
    throw new VerbError('already-released', `release: ACT-${row.id} was released ${row.releasedAt}`);
  }
  if (!isOpen(row.status)) {
    throw new VerbError('invalid-transition', `release: not allowed from ${row.status}`);
  }
  const note = text(input.note);
  if (input.bulk === true && !note) {
    throw new VerbError('note-required', `release: a bulk release needs the shared note`);
  }
  const at = toIso(now);
  const proposed: Record<string, unknown> = {
    [F.releasedAt]: at,
    [F.releasedBy]: actor.upn,
  };
  if (note) proposed[F.releaseNote] = note;
  return diff(row, proposed, { verb: 'release', actor, actedAt: at, note });
};

/** An open row → Retired (terminal, D-13-00-1). The note is required and goes to `ReleaseNote`. */
export const retire: VerbBuilder = (row, actor, input, now) => {
  requireFrom('retire', row, OPEN_STATUSES);
  const note = text(input.note);
  if (!note) throw new VerbError('note-required', `retire: ACT-${row.id} needs a note`);
  const at = toIso(now);
  return diff(
    row,
    { [F.status]: 'Retired', [F.releaseNote]: note },
    { verb: 'retire', actor, actedAt: at, note },
  );
};

/**
 * Done → Accepted, only on the Melbourne calendar day the row was completed ("Done today",
 * D-13-04-4). Clears `CompletedAt`; `Evidence` is untouched. A compensating, audited action.
 */
export const reopen: VerbBuilder = (row, actor, input, now) => {
  requireFrom('reopen', row, ['Done']);
  const at = toIso(now);
  if (!row.completedAt || melbourneDay(row.completedAt) !== melbourneDay(at)) {
    throw new VerbError('reopen-window-closed', `reopen: ACT-${row.id} was not completed today`);
  }
  return diff(
    row,
    { [F.status]: 'Accepted', [F.completedAt]: null },
    { verb: 'reopen', actor, actedAt: at, note: input.note },
  );
};

/**
 * ISS-186 / D-13-R-1 (Andy, 4 Oct 2026): NotMine → Proposed with a new `OwnerRole` and, optionally,
 * a named `Owner` (blank leaves the row to the role). A note is required: it is the reason the new
 * owner reads in the audit trail. `AcceptedAt` is cleared, because the new owner has not accepted
 * it yet. `ReleasedAt` is kept: the row was released once, so it lands straight in the new
 * owner's queue. Oversight only (13-05 `canDoVerb`).
 */
export const reassign: VerbBuilder = (row, actor, input, now) => {
  requireFrom('reassign', row, ['NotMine']);
  const role = input.ownerRole;
  if (!role || !(OWNER_ROLES as readonly string[]).includes(role)) {
    throw new VerbError('owner-role-required', `reassign: ACT-${row.id} needs a new owner role`);
  }
  const note = text(input.note);
  if (!note) throw new VerbError('note-required', `reassign: ACT-${row.id} needs a note`);
  const at = toIso(now);
  const owner = text(input.ownerLookupId ?? undefined);
  return diff(
    row,
    {
      [F.status]: 'Proposed',
      [F.ownerRole]: role,
      [L.owner]: owner || null,
      [F.acceptedAt]: null,
    },
    { verb: 'reassign', actor, actedAt: at, note },
  );
};

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

const BUILDERS: Record<ActionVerb, VerbBuilder> = {
  accept,
  done,
  notMine,
  verify,
  reject,
  decide,
  release,
  retire,
  reopen,
  reassign,
};

/** Build any verb by name. Unknown names throw `unknown-verb`. */
export function buildVerb(
  verb: ActionVerb,
  row: ActionRow,
  actor: Actor,
  input: VerbInput,
  now: ActedAt,
): VerbResult {
  const builder = Object.prototype.hasOwnProperty.call(BUILDERS, verb) ? BUILDERS[verb] : undefined;
  if (!builder) throw new VerbError('unknown-verb', `no verb '${String(verb)}'`);
  return builder(row, actor, input, now);
}
